import React, { useState } from "react";
import axios from "axios";
import { Download, Search } from "lucide-react";
import { toast } from "sonner";

const GyakusoReportPage = () => {
  const [month, setMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  });
  const [rateInput, setRateInput] = useState("");
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [discounted, setDiscounted] = useState(null);
  const [appliedKey, setAppliedKey] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const rate = Number(rateInput);
  const key = `${month}|${rateInput}`;
  const ready = appliedKey === key;
  const params = { month, mufg_tts: rate };
  const number = (value, fractionDigits = 0) =>
    new Intl.NumberFormat("id-ID", { minimumFractionDigits: fractionDigits, maximumFractionDigits: fractionDigits }).format(value ?? 0);

  const preview = async () => {
    if (!/^\d{4}-\d{2}$/.test(month) || !Number.isFinite(rate) || rate <= 0) {
      toast.error("Pilih bulan dan isi kurs MUFG TTS yang benar");
      return;
    }
    setLoading(true);
    setAppliedKey("");
    try {
      const response = await axios.get("/api/reports/gyakuso/preview", { params });
      setRows(response.data.data || []);
      setTotals(response.data.totals || null);
      setDiscounted(response.data.discounted || null);
      setAppliedKey(key);
    } catch (error) {
      setRows([]);
      setTotals(null);
      setDiscounted(null);
      toast.error(error.response?.data?.detail || "Gagal memuat preview Gyakuso");
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!ready || !totals) return;
    setDownloading(true);
    try {
      const response = await axios.get("/api/reports/gyakuso/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = `Gyakuso_${month}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
      toast.success("Report Gyakuso berhasil didownload");
    } catch (error) {
      toast.error("Gagal mendownload report Gyakuso");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Gyakuso</h1>
        <p className="mt-1 text-gray-500">Report harian Invoice Export (C), berdasarkan tanggal CWB.</p>
      </div>
      <div className="bg-white border rounded-xl p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="text-sm font-medium">Bulan report
            <input type="month" className="block mt-1 border rounded-lg p-2" value={month} onChange={(e) => setMonth(e.target.value)} />
          </label>
          <label className="text-sm font-medium">Kurs MUFG TTS (IDR per USD)
            <input type="number" min="0.01" step="0.01" className="block mt-1 border rounded-lg p-2 w-52" value={rateInput}
              onChange={(e) => setRateInput(e.target.value)} placeholder="Isi kurs akhir bulan" />
          </label>
          <button type="button" disabled={loading} onClick={preview}
            className="flex items-center gap-2 bg-blue-600 text-white rounded-lg px-4 py-2 disabled:opacity-50">
            <Search size={16} />{loading ? "Memuat..." : "Preview"}
          </button>
          <button type="button" disabled={!ready || !totals || downloading} onClick={download}
            className="flex items-center gap-2 bg-green-700 text-white rounded-lg px-4 py-2 disabled:opacity-50">
            <Download size={16} />{downloading ? "Menyiapkan..." : "Download Excel"}
          </button>
        </div>
        {ready && (
          <div className="text-sm text-gray-600 space-y-1">
            <p>Kurs yang digunakan: Rp{number(rate, 2)} per US$.</p>
            <p>C.W. memakai berat tagihan (`weight_kg`). Potongan US$ dari Freight Charge:
              Boshoku {discounted?.boshoku_awb || 0} AWB × 15%, Rayspeed {discounted?.rayspeed_awb || 0} AWB × 20%; total US${number(totals?.discount_usd, 2)}.
            </p>
          </div>
        )}
      </div>
      {ready && (
        <div className="bg-white border rounded-xl overflow-hidden">
          <div className="max-h-[600px] overflow-auto">
            <table className="min-w-[950px] w-full text-sm">
              <thead className="sticky top-0 bg-gray-100 z-10">
                <tr>{["DATE", "PCS", "GRAMS", "US$", "FREIGHT CHARGE", "CHARGE IN EMERGENCY"].map((name) =>
                  <th key={name} className="p-3 text-right first:text-left whitespace-nowrap">{name}</th>)}</tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.date} className="border-t">
                    <td className="p-3 whitespace-nowrap">{row.date}</td>
                    <td className="p-3 text-right">{number(row.pcs)}</td>
                    <td className="p-3 text-right">{number(row.grams)}</td>
                    <td className="p-3 text-right">{number(row.usd, 2)}</td>
                    <td className="p-3 text-right">{number(row.freight, 2)}</td>
                    <td className="p-3 text-right">{number(row.emergency, 2)}</td>
                  </tr>
                ))}
                {totals && (
                  <tr className="border-t-2 bg-blue-50 font-bold">
                    <td className="p-3">TOTAL</td>
                    <td className="p-3 text-right">{number(totals.pcs)}</td>
                    <td className="p-3 text-right">{number(totals.grams)}</td>
                    <td className="p-3 text-right">{number(totals.usd, 2)}</td>
                    <td className="p-3 text-right">{number(totals.freight, 2)}</td>
                    <td className="p-3 text-right">{number(totals.emergency, 2)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default GyakusoReportPage;
